class SkeletonLoadingManager {
    constructor() {
        this.lcpLoaded = false;
        this.skeletonElements = [];
        this.observer = null;
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        this.findSkeletonElements();
        this.handleRemoveSkeleton();
    }

    findSkeletonElements() {
        this.skeletonElements = document.querySelectorAll('.skeleton-loader');

        // Initialize all skeleton elements as loading
        this.skeletonElements.forEach(element => {
            element.classList.add('lcp-loading');
            element.classList.remove('lcp-loaded');
        });
    }

    setupImageLoadingObserver() {
        // Create intersection observer to handle images coming into view
        const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    const skeletonParent = img.closest('.skeleton-loader');

                    if (skeletonParent && this.lcpLoaded) {
                        this.showContent(skeletonParent);
                    }

                    // Stop observing this image
                    imageObserver.unobserve(img);
                }
            });
        }, {
            rootMargin: '50px'
        });

        // Observe all images within skeleton loaders
        this.skeletonElements.forEach(skeleton => {
            const images = skeleton.querySelectorAll('img');
            images.forEach(img => {
                if (img.complete) {
                    // Image already loaded
                    if (this.lcpLoaded) {
                        this.showContent(skeleton);
                    }
                } else {
                    // Observe for lazy loading
                    imageObserver.observe(img);

                    // Also listen for load event
                    img.addEventListener('load', () => {
                        if (this.lcpLoaded) {
                            this.showContent(skeleton);
                        }
                    });
                }
            });
        });
    }

    handleRemoveSkeleton() {
        // Fallback method using window load event
        if (document.readyState === 'complete') {
            setTimeout(() => this.handleLCPComplete(), 100);
        } else {
            window.addEventListener('load', () => {
                setTimeout(() => this.handleLCPComplete(), 100);
            });
        }
    }

    handleLCPComplete() {
        if (this.lcpLoaded) return;

        this.lcpLoaded = true;

        // Check which skeleton elements can be shown
        this.skeletonElements.forEach(element => {
            this.showContent(element);
        });

        // Clean up observer
        if (this.observer) {
            this.observer.disconnect();
        }

        // Dispatch custom event for other scripts to listen to
        document.dispatchEvent(new CustomEvent('lcp-complete'));
    }

    checkAndShowContent(skeletonElement) {
        const images = skeletonElement.querySelectorAll('img');

        if (images.length === 0) {
            // No images, show content immediately
            this.showContent(skeletonElement);
            return;
        }

        // Check if all images are loaded or if any critical image is loaded
        const allImagesLoaded = Array.from(images).every(img => img.complete);
        const hasVisibleImage = Array.from(images).some(img => img.complete && img.naturalWidth > 0);

        if (allImagesLoaded || hasVisibleImage) {
            this.showContent(skeletonElement);
        }
    }

    showContent(skeletonElement) {
        skeletonElement.classList.remove('lcp-loading');
        skeletonElement.classList.add('lcp-loaded');
    }

    // Public methods for manual control
    static showAllContent() {
        document.querySelectorAll('.skeleton-loader.lcp-loading').forEach(element => {
            element.classList.remove('lcp-loading');
            element.classList.add('lcp-loaded');
        });
    }

    static addSkeletonElement(element) {
        if (element && !element.classList.contains('skeleton-loader')) {
            element.classList.add('skeleton-loader', 'lcp-loading');

            // If LCP already completed, show immediately
            if (window.skeletonManager && window.skeletonManager.lcpLoaded) {
                window.skeletonManager.checkAndShowContent(element);
            }
        }
    }

    static removeSkeletonElement(element) {
        if (element) {
            element.classList.remove('skeleton-loader', 'lcp-loading', 'lcp-loaded');
        }
    }
}

if (!window.skeletonManager) {
    window.skeletonManager = new SkeletonLoadingManager();
}
